import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../../../../components/Common/UI/Accordion';
import Icons from '../../../../../../components/Common/Icons/Icons';
import { cn } from '../../../../../../utils/styles';
import { AccountManagementSubRow, IDeliveryType, INotificationType } from '../../columns';
import DeliverySettings from './Settings';
import ModalEditDelivery from './Modals/Edit';
import ModalPaperlessHistory from '../Modals';

interface IDeliveryPreference {
  selected: AccountManagementSubRow | undefined;
}

export interface IDeliveryModal {
  statement: 'checkingStatement' | 'visa' | 'mortgage' | 'notices';
  type: 'edit' | 'history';
  open: boolean;
}

function ShowStatus({
  data = undefined,
  name,
}: {
  data?: AccountManagementSubRow;
  name: keyof AccountManagementSubRow;
}) {
  return (
    <>
      {data && (data[name] === 'Both' || data[name] === 'Paper') ? (
        <div
          className={cn('w-[30px] flex items-center justify-center h-[30px] border rounded-[3px] border-status-late')}
        >
          P
        </div>
      ) : null}
      {data && (data[name] === 'Both' || data[name] === 'Electronic') ? (
        <div
          className={cn(
            'w-[30px] flex items-center justify-center h-[30px] border rounded-[3px] ' +
              'border-status-completed text-status-completed',
          )}
        >
          E
        </div>
      ) : null}
    </>
  );
}

function DeliveryPreference({ selected }: IDeliveryPreference) {
  const { hash } = useLocation();
  const [openAccordion, setOpenAccordion] = useState<boolean>(true);
  const [data, setData] = useState<AccountManagementSubRow>();
  // const hashValue = (hash || '').replace('#', '');

  const [modalState, setModalState] = useState<IDeliveryModal>({
    statement: 'checkingStatement',
    type: 'edit',
    open: false,
  });

  const handleOpen = ({ open, statement, type }: IDeliveryModal) => {
    setModalState({ statement, open, type });
  };

  const handleSave = (
    notifyType: INotificationType,
    deliveryType: IDeliveryType,
    statement: IDeliveryModal['statement'],
  ) => {
    setData(prevData => {
      if (!prevData) return prevData;

      return {
        ...prevData,
        notification: notifyType,
        [statement]: deliveryType,
      };
    });
  };

  useEffect(() => {
    setData(selected);
  }, [selected]);

  return (
    <div>
      <Accordion type="single" collapsible defaultValue="item-key">
        <AccordionItem key="key" value="item-key" className="p-0 border rounded-lg overflow-hidden">
          <AccordionTrigger className="p-0" onClick={() => setOpenAccordion(!openAccordion)}>
            <div
              className="flex items-center w-[405px] gap-[20px] bg-[#5582B42E] p-[10px] min-h-[64px]
            justify-between"
            >
              <div className="pl-[10px] font-bold text-primary text-[20px]">Delivery Preference</div>
              <Icons.ArrowRight
                className={cn('rotate-90', {
                  'rotate-[-90deg]': openAccordion,
                })}
              />
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="grid gap-[10px] p-[20px] text-[16px]">
              <div className="grid grid-cols-[200px,auto] gap-[10px]">
                <div className="opacity-80 text-primary">Checking Statement:</div>
                <div className="flex items-center gap-[10px]">
                  <ShowStatus data={data} name="checkingStatement" />
                </div>
              </div>

              <div className="grid grid-cols-[200px,auto] gap-[10px]">
                <div className="opacity-80 text-primary">eTax:</div>
                <div className="flex items-center gap-[10px]">
                  <ShowStatus data={data} name="eTax" />
                </div>
              </div>

              <div className="grid grid-cols-[200px,auto] gap-[10px]">
                <div className="opacity-80 text-primary">Notices:</div>
                <div className="flex items-center gap-[10px]">
                  <ShowStatus data={data} name="notices" />
                  <DeliverySettings statement="notices" handleOpen={handleOpen} />
                </div>
              </div>

              <div className="grid grid-cols-[200px,auto] gap-[10px]">
                <div className="opacity-80 text-primary">Mortgage Statements:</div>
                <div className="flex items-center gap-[10px]">
                  <ShowStatus data={data} name="mortgage" />
                  <div
                    className={cn(
                      'w-[30px] flex items-center justify-center h-[30px] border rounded-[3px] ' +
                        'border-status-inactive text-status-inactive',
                    )}
                  >
                    <Icons.BellFilledOff className={cn('h-[18px]')} />
                  </div>
                  <DeliverySettings statement="mortgage" handleOpen={handleOpen} />
                </div>
              </div>

              <div className="grid grid-cols-[200px,auto] gap-[10px]">
                <div className="opacity-80 text-primary">Loan:</div>
                <div className="flex items-center gap-[10px]">
                  <ShowStatus data={data} name="loan" />
                </div>
              </div>

              <div className="grid grid-cols-[200px,auto] gap-[10px]">
                <div className="opacity-80 text-primary">Visa:</div>
                <div className="flex items-center gap-[10px]">
                  <ShowStatus data={data} name="visa" />
                  <div
                    className={cn(
                      'w-[30px] flex items-center justify-center h-[30px] border rounded-[3px] ' +
                        'border-status-inactive text-status-inactive',
                    )}
                  >
                    <Icons.BellFilled className={cn('h-[18px]')} />
                  </div>
                  <DeliverySettings statement="visa" handleOpen={handleOpen} />
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      {data && modalState.type === 'edit' ? (
        <ModalEditDelivery
          statement={modalState.statement}
          open={modalState.open}
          handleSave={handleSave}
          handleOpen={handleOpen}
          data={data}
        />
      ) : null}
      {data && modalState.type === 'history' ? (
        <ModalPaperlessHistory open={modalState.open} handleOpen={handleOpen} />
      ) : null}
    </div>
  );
}

export default DeliveryPreference;
